
import { singularityStore, uiStore } from "@/store/store";
import Vue from "vue";
import { Component } from "vue-property-decorator";
import Logo from "@/components/Singularity/components/Logo.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { SiteStateV } from "@/futurelayer/settings/types";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import * as t from "io-ts";
import { getFutureLayerAppUrlWithReturnTo } from "@/futurelayer/app/app";
import { getFrontendUrl } from "@/lib/document";
import { switchToAdvancedMode } from "@/futurelayer/ui/ui";
import { configStore } from "@/store/store-accessor";
import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { logEvent } from "@/util/tracking";
import { isBreakdanceDevelopmentEnvironment } from "@/lib/dev/development-environment";
import { openUiPanel } from "@/lib/panels/panels";

export const PHONE_PORTRAIT_BREAKPOINT_ID = "breakpoint_phone_portrait";

@Component({
  components: {
    Logo,
    NiceButton
  }
})
export default class FutureLayerLogoMenu extends Vue {
  isOpen = false;
  isLoading = false;

  get devEnvironment() {
    return isBreakdanceDevelopmentEnvironment();
  }

  get siteState() {
    return singularityStore.siteStatus.siteState;
  }

  get isClaimed() {
    if (!this.siteState || 'error' in this.siteState) return false;
    return this.siteState.type === 'claimed';
  }

  get singularityDebugPanelOpen() {
    return (
      singularityStore.singularityUiState.singularityRightPanel === "debug"
    );
  }

  get isMobile() {
    return uiStore.currentBreakpoint.id === PHONE_PORTRAIT_BREAKPOINT_ID;
  }

  get isZoomActive() {
    return singularityStore.canvasZoom;
  }

  get sidebarPanelsEnabled() {
    return singularityStore.debugSidebarPanelsEnabled;
  }

  get isHistoryPanelOpen() {
    return uiStore.panelState.leftSidebarPanel === "history" ||
           uiStore.panelState.rightSidebarPanel === "history";
  }

  get userData() {
    if (this.siteState && "user_data" in this.siteState) {
      return this.siteState.user_data;
    } else {
      return null;
    }
  }

  get credits() {
    if (!this.siteState) return 0;
    if ("error" in this.siteState) return 0;
    if (this.siteState.type === "anonymous") return this.siteState.credits;
    if (this.siteState.type === "claimed")
      return this.siteState.user_data.credits;
    return 0;
  }

  get planSlug() {
    if (!this.siteState || "error" in this.siteState) return null;
    if (this.siteState.type === "claimed") {
      return this.siteState.user_data.plan_slug;
    }
    return null;
  }

  get planDisplayName() {
    if (!this.planSlug) return "";

    // Capitalize first letter of plan slug
    return this.planSlug.charAt(0).toUpperCase() + this.planSlug.slice(1);
  }

  manageAccount() {
    window.location.href = getFutureLayerAppUrlWithReturnTo("/");
    this.isOpen = false;
  }

  onToggle(isOpen: boolean) {
    if (isOpen) {
      this.fetchSiteState();
    }
  }

  async fetchSiteState() {
    this.isLoading = true;

    try {
      const result = await makeWordPressAjaxRequest({
        action: "futurelayer_get_site_state_from_app",
        decoder: t.type({
          success: t.boolean,
          data: SiteStateV
        }),
        suppressErrorLogging: false,
        friendlyName: "Get Site State from FutureLayer App"
      });

      if (result._tag === "Right" && result.right.success) {
        singularityStore.setSiteStatus({
          ...singularityStore.siteStatus,
          siteState: result.right.data
        });
      }
    } catch (error) {
      console.error("Failed to fetch site state:", error);
    } finally {
      this.isLoading = false;
    }
  }

  toggleSingularityDebugPanel() {
    singularityStore.setSingularityRightPanel(
      this.singularityDebugPanelOpen ? null : "debug"
    );
    this.isOpen = false;
  }

  toggleAIJobManager() {
    singularityStore.setSingularityRightPanel(
      singularityStore.singularityUiState.singularityRightPanel ===
        "ai_job_manager"
        ? null
        : "ai_job_manager"
    );
    this.isOpen = false;
  }

  openSettings() {
    singularityStore.setSingularityFullWidthPanel("settings");
    this.isOpen = false;
  }

  get frontendUrl() {
    return getFrontendUrl();
  }

  get backToAdmin() {
    if (configStore.documentSpecificNeededUrls) {
      return configStore.documentSpecificNeededUrls.backToWordPressUrl;
    }

    return "";
  }

  openFrontend() {
    const url = this.frontendUrl;
    if (url) {
      window.open(url, "_blank");
    }
    this.isOpen = false;
  }

  toggleAdvancedMode() {
    switchToAdvancedMode();
    this.isOpen = false;
  }

  toggleMobileBreakpoint() {
    const breakpoint = this.isMobile
      ? configStore.breakpoints.find(b => b.id === BASE_BREAKPOINT_ID)
      : configStore.breakpoints.find(
          b => b.id === PHONE_PORTRAIT_BREAKPOINT_ID
        );

    if (!breakpoint) return;

    singularityStore.setCanvasZoom(false);

    uiStore.changeBreakpoint(breakpoint);

    logEvent("FutureLayer Mobile Preview Toggle");
    this.isOpen = false;
  }

  toggleZoomLevel() {
    singularityStore.toggleCanvasZoom();
    logEvent("FutureLayer Zoom Toggle");
    this.isOpen = false;
  }

  toggleHistoryPanel() {
    if (this.isHistoryPanelOpen) {
      // Close history panel
      if (uiStore.panelState.leftSidebarPanel === "history") {
        uiStore.setLeftSidebarState("closed");
      }
      if (uiStore.panelState.rightSidebarPanel === "history") {
        uiStore.setRightSidebarState("closed");
      }
    } else {
      // Open history panel
      openUiPanel("history");
    }
    this.isOpen = false;
  }

  toggleSidebarPanels() {
    singularityStore.toggleDebugSidebarPanels();
    this.isOpen = false;
  }
}
